/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import deepEql from 'deep-eql'
import * as Array from 'effect/Array'
import { NonEmptyArray } from 'effect/Array'
import * as String from 'effect/String'
import Engine from 'publicodes'
import { Dispatch, Middleware } from 'redux'

import { isComparateurConfig } from '@/domaine/ComparateurConfig'
import { détermineLesProchainesQuestions } from '@/domaine/engine/détermineLesProchainesQuestions'
import { getOrCreateEnginePromise } from '@/domaine/engine/engineCache'
import {
	Action,
	applicabilitéDesQuestionsRépondues,
	metÀJourLesQuestionsSuivantes,
} from '@/store/actions/actions'
import {
	PublicodesSimulationConfig,
	RootState,
	SituationPublicodes,
} from '@/store/reducers/rootReducer'
import { Simulation } from '@/store/reducers/simulation.reducer'
import { completeSituationSelector } from '@/store/selectors/completeSituation.selector'
import { completeSituationsSelonContextesSelector } from '@/store/selectors/completeSituationSelonContextes.selector'
import { complement } from '@/utils/complement'
import { safeSetSituation } from '@/utils/publicodes/safeSetSituation'

let lastSimulation: Simulation | null = null
let lastConfig: PublicodesSimulationConfig | null = null
let lastSituationsAvecContextes: NonEmptyArray<SituationPublicodes> | null =
	null
let engines: NonEmptyArray<Engine> | null = null

export const prendLaProchaineQuestionMiddleware: Middleware<
	object,
	RootState,
	Dispatch<Action>
> = (store) => (next) => async (action: Action) => {
	const result = next(action)

	const newState = store.getState()

	const simulation = newState.simulation
	const situation = completeSituationSelector(newState)
	const questionsRépondues = simulation?.questionsRépondues
	const questionsSuivantesActuelles = simulation?.questionsSuivantes || []

	const config = simulation?.config

	if (!config) {
		return result
	}

	const engine = await getOrCreateEnginePromise(config.nomModèle!)
	const configHasChanged = lastConfig !== config

	if (config && configHasChanged) {
		engines = isComparateurConfig(config)
			? (config.contextes.map(() =>
					engine.shallowCopy()
				) as NonEmptyArray<Engine>)
			: [engine]
		lastSituationsAvecContextes = null
	}

	if (action.type === 'CONFIGURE_LA_SIMULATION') {
		lastConfig = null
		lastSituationsAvecContextes = null
		lastSimulation = null
	}

	if (situation && config && engines && simulation !== lastSimulation) {
		const situationsAvecContextes =
			completeSituationsSelonContextesSelector(newState)

		const situationAChangé =
			!!lastSituationsAvecContextes &&
			!deepEql<
				NonEmptyArray<SituationPublicodes>,
				NonEmptyArray<SituationPublicodes>
			>(situationsAvecContextes, lastSituationsAvecContextes)

		if (!lastSituationsAvecContextes || situationAChangé) {
			lastSituationsAvecContextes = situationsAvecContextes

			engines.forEach((engine, index) => {
				safeSetSituation(
					(s) => engine.setSituation(s),
					situationsAvecContextes[index]
				)
			})

			lastSimulation = simulation
			lastConfig = config

			store.dispatch(
				applicabilitéDesQuestionsRépondues(
					(questionsRépondues || []).map((question) => ({
						...question,
						applicable:
							engine.evaluate({ 'est applicable': question.règle })
								.nodeValue === true,
					}))
				)
			)

			const prochainesQuestions = détermineLesProchainesQuestions(
				engines,
				config,
				questionsRépondues
			)

			if (
				arraysAreDifferent(prochainesQuestions, questionsSuivantesActuelles)
			) {
				store.dispatch(metÀJourLesQuestionsSuivantes(prochainesQuestions))
			}
		}
	}

	return result
}

const arraysAreDifferent = complement(Array.getEquivalence(String.Equivalence))
